import { getLayoutMode } from '@/utils/deviceDetection';

/**
 * Immer draft state type for slide/side panel mutations. Also the shared base
 * of the artifact slices' widened `SetState` types — extend it there instead
 * of re-declaring these panel fields per slice.
 */
export type PanelDraftState = {
  ui: {
    sidePanel: { isOpen: boolean };
    slidePanel: {
      isMinimized: boolean;
      previewMode: boolean;
    };
    mapControls: { isLayersExpanded: boolean };
    sharePanel: { isOpen: boolean };
  };
};

/**
 * Base mechanics for openDetails(): close layers panel (single-sidepanel rule),
 * unminimize + close sidePanel on mobile.
 *
 * Intentionally does NOT touch `previewMode` — that is a per-artifact product
 * decision. Artifacts that want preview on mobile (e.g. tasks) must also call
 * `applyDetailsPreviewOnMobile` after this. Artifacts that open fully expanded
 * (e.g. notes) call only this helper and rely on preceding close/reset helpers
 * to clear `previewMode`.
 */
export function applyOpenDetailsPanel(state: PanelDraftState): void {
  state.ui.mapControls.isLayersExpanded = false;
  // Manager list only — the share CONFIG form is guard-closed by the opener
  // (withShareFormGuard). Do NOT close the form in-draft: it may be dirty and
  // the unsaved-changes prompt must run outside set().
  state.ui.sharePanel.isOpen = false;
  state.ui.slidePanel.isMinimized = false;
  if (getLayoutMode() === 'mobile') {
    state.ui.sidePanel.isOpen = false;
  }
}

/**
 * Opt-in: put the details panel into preview mode on mobile.
 * Call AFTER `applyOpenDetailsPanel` for artifacts that should open in
 * partial (half-height) expansion on mobile. Desktop is unaffected.
 */
export function applyDetailsPreviewOnMobile(state: PanelDraftState): void {
  if (getLayoutMode() === 'mobile') {
    state.ui.slidePanel.previewMode = true;
  }
}

/** Call in closeDetails() — resets previewMode. */
export function applyCloseDetailsPanel(state: PanelDraftState): void {
  state.ui.slidePanel.previewMode = false;
}

/**
 * Shared closeDetails body: guard → close panel → clear selection.
 * Called inside `set()` with the Immer draft state and the artifact namespace.
 */
export function applyCloseDetails(
  state: PanelDraftState,
  ns: { detailsIsOpen: boolean; selectedId: string | null }
): void {
  if (!ns.detailsIsOpen) return;
  ns.detailsIsOpen = false;
  applyCloseDetailsPanel(state);
  ns.selectedId = null;
}

/**
 * Call in openForm() — closes layers panel, optionally minimizes on mobile,
 * resets previewMode.
 *
 * `minimizeOnMobile` defaults to `true` (task form expects to open minimized
 * with Cancel/Next collapsed actions). Pass `false` for forms that should
 * open in full mobile state (e.g. note form).
 */
export function applyOpenFormPanel(
  state: PanelDraftState,
  options?: { minimizeOnMobile?: boolean }
): void {
  const minimizeOnMobile = options?.minimizeOnMobile ?? true;
  state.ui.mapControls.isLayersExpanded = false;
  // Manager list only — see applyOpenDetailsPanel: the share CONFIG form is
  // guard-closed by the opener (withShareFormGuard), never in-draft here.
  state.ui.sharePanel.isOpen = false;
  if (minimizeOnMobile && getLayoutMode() === 'mobile') {
    state.ui.slidePanel.isMinimized = true;
  }
  state.ui.slidePanel.previewMode = false;
}

/** Call in closeForm() — resets isMinimized and previewMode. */
export function applyCloseFormPanel(state: PanelDraftState): void {
  state.ui.slidePanel.isMinimized = false;
  state.ui.slidePanel.previewMode = false;
}

// ===== CROSS-ARTIFACT MUTUAL EXCLUSION =====

/** Minimal slice shape required by `closeCompetingArtifacts`. */
export interface ClosableArtifactSlice {
  closeDetails: () => void;
  closeForm: () => void;
}

/**
 * Close all competing artifact panels (details + form) except the one
 * identified by `currentArtifactId`. Call from `openDetails` and `openForm`
 * to enforce the single-panel rule across artifacts.
 *
 * The `otherArtifacts` array lists only the artifact slices that should be
 * closed — callers build it from `get()` to avoid a circular import through
 * the artifact registry.
 *
 * Usage:
 * ```ts
 * const { tasks, notes } = get();
 * closeCompetingArtifacts([tasks, notes]);
 * ```
 */
export function closeCompetingArtifacts(
  otherArtifacts: readonly ClosableArtifactSlice[]
): void {
  for (const artifact of otherArtifacts) {
    artifact.closeDetails();
    artifact.closeForm();
  }
}

// ===== SHARE FORM SINGLE-PANEL GUARD =====

/** Minimal store shape required by `withShareFormGuard`. */
export interface ShareFormGuardStore {
  shareForm: { formIsOpen: boolean };
  formConflict: { request: (onProceed: () => void) => void };
}

/**
 * Enforce the single right-panel rule against the share config form when
 * opening (or toggling) another panel. The share config form lives in its own
 * slice and is rendered independently of the share manager's `isOpen` flag, so
 * the panels that close the manager must also close the form — otherwise it
 * stays on screen.
 *
 * The share form is the only non-artifact form and at most one form is ever
 * open, so routing through the global guard here closes (and, when dirty,
 * prompts about) exactly that form before running `onProceed`. When no share
 * form is open, `onProceed` runs immediately — leaving artifact↔artifact and
 * layer-stacking flows untouched.
 */
export function withShareFormGuard(
  store: ShareFormGuardStore,
  onProceed: () => void
): void {
  if (store.shareForm.formIsOpen) {
    store.formConflict.request(onProceed);
  } else {
    onProceed();
  }
}

/**
 * Shared select-item logic: set selectedId, then either fetch or
 * normalize shortId→GUID when data is already cached.
 *
 * Generic over the Zustand store draft so each slice can pass its own
 * `set` without type coercion at the call site.
 *
 * When the new `id` does not match the currently cached detail object,
 * `clearStaleDetail` is invoked (same `set` pass as `setSelectedId`) to drop
 * that stale detail BEFORE the fetch starts. This keeps the invariant "the
 * detail object matches the current selection, or is null": a (re)mounting
 * detail panel must never render — or re-fetch by `.id` — the previously
 * selected item while the new one loads. Without it, the panel's mount-time
 * fetch races the slice's fetch and the two flip the detail object back and
 * forth forever (selecting item B remounts the panel holding stale item A).
 */
export function handleSelectItem<S>(
  set: (fn: (state: S) => void) => void,
  id: string | null,
  setSelectedId: (draft: S, id: string | null) => void,
  cached: { id: string; shortId: string } | null,
  fetchById: (id: string) => void,
  clearStaleDetail?: (draft: S) => void
): void {
  const isDifferentItem =
    id !== null && cached?.id !== id && cached?.shortId !== id;

  set((state) => {
    setSelectedId(state, id);
    if (isDifferentItem) clearStaleDetail?.(state);
  });

  if (id !== null) {
    if (isDifferentItem) {
      fetchById(id);
    } else if (cached && id !== cached.id) {
      set((state) => setSelectedId(state, cached.id));
    }
  }
}

/**
 * Shared post-fetch cleanup for list-backed artifact slices. Call inside the
 * `set()` of a filtered list fetch (e.g. `fetchNotes`, `fetchObstructions`).
 *
 * Drops the stale list highlight when the selected item is no longer in the
 * results — but ONLY when nothing is open. An open & loaded detail panel or
 * edit form must keep its data even if filters exclude its item from the
 * current page (panel open-state is driven by `detailsIsOpen`/`formIsOpen`,
 * not `selectedId`). `clearDetailed` lets each slice null its own detail
 * object, whose field name differs per artifact (`detailedNote` /
 * `detailedObstruction`).
 */
export function applyClearStaleSelection<TItem extends { id: string }>(
  ns: {
    selectedId: string | null;
    detailsIsOpen: boolean;
    formIsOpen: boolean;
    items: TItem[];
  },
  clearDetailed: () => void
): void {
  if (
    ns.selectedId !== null &&
    !ns.detailsIsOpen &&
    !ns.formIsOpen &&
    !ns.items.some((item) => item.id === ns.selectedId)
  ) {
    ns.selectedId = null;
    clearDetailed();
  }
}
